import { Injectable, Injector, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController } from "@ionic/angular";
import { BehaviorSubject, Subject } from "rxjs";
import { isTraining } from "../auth/binding-state";
import { ClientTokenService } from "../auth/client-token.service";
import { ComService } from "../com/com.service";
import { ConfigService } from "../config/config.service";
import { EnvironmentService } from "../env/env.service";
import { FlightTimerService } from "../flight-timer/flight-timer.service";
import { SettingsService } from "../settings/settings.service";
import {
  AUTO_ACCEPT_OFF_KEY,
  isAutoAcceptIdleRoute,
  nextAutoAccept,
  parseMissionSnapshot,
  readAutoAcceptOn,
  skipOpenMissions,
} from "./mission-popups";

// The window that creates a flight for each new mission without a tap. The
// mission-monitor wallboard gives this name to exactly one iframe (panel prop
// `name`). `window.name` survives navigation and reloads inside the frame, and
// each iframe has its own, which localStorage and sessionStorage do not.
export const AUTO_ACCEPT_WINDOW_NAME = "efb-auto-accept";
// The ids this window already handed out, kept for the tab's life so a reload
// does not create a second flight for a mission whose acknowledge failed.
const AUTO_ACCEPT_TRIED_KEY = "efb.autoAcceptTried";
// When the switch last went on. Missions older than that (less a clock slack)
// never get a flight, also when the open list arrives only after the switch
// (reload in the same tab, socket gap).
const AUTO_ACCEPT_SINCE_KEY = "efb.autoAcceptSince";
// How often an open mission socket is replaced. A browser cannot see the
// server's pings, so a socket whose network path died without a close event
// stays open and the list goes stale. A window that is always in front (the
// wallboard) never resumes, so nothing else finds that socket.
// ponytail: a new mission can be up to this late after a silent drop. A
// server heartbeat frame plus a client timeout makes it seconds.
const MISSION_SOCKET_REFRESH_MS = 5 * 60_000;

@Injectable({
  providedIn: "root",
})
export class MissionService {
  private settings = inject(SettingsService);
  private com = inject(ComService);
  private envService = inject(EnvironmentService);
  private configSer = inject(ConfigService);
  private clientToken = inject(ClientTokenService);
  private alertser = inject(AlertController);

  [x: string]: any;

  public alert: Subject<MissionInterface> = new Subject();

  // The open missions of the selected base, pushed by mission-go over
  // GET /v1/mission/ws. Only backends with `missionApiKey` have that route; on
  // the others this stays empty and the legacy fetch path runs.
  public readonly open$ = new BehaviorSubject<MissionInterface[]>([]);
  // The mission a popup tap asked the flow-briefing page to show.
  public readonly focus$ = new BehaviorSubject<number | null>(null);
  private socket?: WebSocket;
  private socketUrl = "";
  /** A base change is pending: the new base's first snapshot gets no flight. */
  private skipNextSnapshot = false;
  private retryMs = 1000;
  private retryTimer?: ReturnType<typeof setTimeout>;

  public readonly autoAccept = typeof window !== "undefined" && window.name === AUTO_ACCEPT_WINDOW_NAME;
  private autoTried = new Set<number>(this.autoAccept ? readTried() : []);
  /** The on/off switch of the auto-accept window, kept in localStorage. */
  public autoAcceptOn = readAutoAcceptOn(typeof localStorage !== "undefined" ? localStorage : undefined);
  private autoAcceptSince = readSince();
  /** The mission this window is creating a flight for, if any. */
  public autoPendingId?: number;
  private router = inject(Router);
  private injector = inject(Injector);

  constructor() {
    if (this.autoAccept) {
      this.open$.subscribe(() => this.pumpAutoAccept());
      // A mission that waited for a busy screen gets its turn once the screen is free.
      setInterval(() => this.pumpAutoAccept(), 5_000);
    }
    this.settings.settings$.subscribe(() => this.watchMissions().catch((err) => console.error("watchMissions: ", err)));
    // A new binding token can turn training mode on or off.
    this.clientToken.claimsObs.subscribe(() =>
      this.watchMissions().catch((err) => console.error("watchMissions: ", err))
    );
    setInterval(
      () => this.refreshSocket().catch((err) => console.error("watchMissions: ", err)),
      MISSION_SOCKET_REFRESH_MS
    );
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        // A socket that lived through a suspend can be dead without a close
        // event, so a resume always opens a new one.
        this.retryMs = 1000;
        this.watchMissions(true).catch((err) => console.error("watchMissions: ", err));
      });
    }
  }

  // Replaces the mission socket, if one is wanted. Each new socket gets the
  // full snapshot on connect, so no mission is lost or shown twice.
  private refreshSocket(): Promise<void> {
    return this.socketUrl ? this.watchMissions(true) : Promise.resolve();
  }

  // No real mission reaches a device in training mode: neither the socket nor
  // the poll runs. The test mission stays.
  private async inTraining(): Promise<boolean> {
    return isTraining(
      await this.settings.getSetting("registration"),
      this.clientToken.getClaims()?.bound,
      (await this.configSer.configP).general.testDutyAircraftReg
    );
  }

  // Opens the mission socket for the current backend and base, or keeps the
  // one already open for them unless `reopen` is set. Safe to call at any time.
  // A socket that dies while the app stays in front is replaced by the next
  // periodic refresh, resume or pull-to-refresh.
  public async watchMissions(reopen = false): Promise<void> {
    const api = await this.missionApi();
    const base = await this.missionBase();
    const mission = (await this.envService.getApiInfo()).mission ?? "";
    const url =
      api.key && base && mission && !(await this.inTraining())
        ? mission.replace(/^http/, "ws") + "/v1/mission/ws?base=" + encodeURIComponent(base)
        : "";
    if (!reopen && url === this.socketUrl && (this.socket || this.retryTimer)) return;

    // Another base's missions must not stay on screen, or be refused, while
    // the new socket connects.
    if (url !== this.socketUrl) this.open$.next([]);
    // The base is one setting for every window of the origin, so another
    // window can change it here. As at switch-on, only later missions get a flight.
    if (this.autoAccept && this.socketUrl && url && url !== this.socketUrl) this.skipNextSnapshot = true;
    this.socketUrl = url;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    const old = this.socket;
    this.socket = undefined;
    old?.close();
    if (!url) return;

    const ws = new WebSocket(url, ["efb-mission.v1", "key." + api.key]);
    this.socket = ws;
    ws.onopen = () => (this.retryMs = 1000);
    ws.onmessage = (e) => {
      const missions = parseMissionSnapshot(e.data);
      if (!missions || this.socket !== ws) return;
      if (this.skipNextSnapshot) {
        this.skipNextSnapshot = false;
        this.autoTried = skipOpenMissions(missions, this.autoTried);
        writeTried(this.autoTried);
      }
      this.open$.next(missions);
    };
    ws.onclose = () => {
      if (this.socket !== ws) return;
      // ponytail: the last snapshot stays shown while offline. The server
      // does NOT refuse an ack for a mission already read, so a Refuse on a
      // stale list overwrites the registration that took the mission.
      // Closing that needs a server-side state check on acknowledge.
      this.socket = undefined;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = undefined;
        this.watchMissions().catch((err) => console.error("watchMissions: ", err));
      }, this.retryMs);
      this.retryMs = Math.min(this.retryMs * 2, 30_000);
    };
  }

  // One mission at a time: the create form is a page, and a second alert would
  // navigate away from a create in progress. Only the create for the pending
  // mission releases the next one, and nothing releases it on a timer: a create
  // that waits for input (a POB below 1) holds the queue until someone answers.
  // Each mission is tried once per tab, so a failed create or acknowledge does
  // not loop, and a reload does not repeat it.
  private pumpAutoAccept(): void {
    if (!this.autoAcceptOn) return;
    if (this.autoPendingId !== undefined || !this.autoAcceptIdle()) return;
    const m = nextAutoAccept(this.open$.value, this.autoTried, Date.now(), this.autoAcceptSince);
    if (!m) return;
    this.autoTried.add(m.id);
    writeTried(this.autoTried);
    this.autoPendingId = m.id;
    this.alert.next(m);
  }

  /** Switching on skips the missions already open, so only later ones get a flight.
   *  A create already pending is not cancelled when switching off. */
  public setAutoAcceptOn(on: boolean): void {
    this.autoAcceptOn = on;
    try {
      if (on) localStorage.removeItem(AUTO_ACCEPT_OFF_KEY);
      else localStorage.setItem(AUTO_ACCEPT_OFF_KEY, "1");
    } catch {
      // storage can throw; the switch still holds for this session
    }
    if (on) {
      this.autoAcceptSince = Date.now();
      try {
        sessionStorage.setItem(AUTO_ACCEPT_SINCE_KEY, String(this.autoAcceptSince));
      } catch {
        // ponytail: without storage a reload forgets the switch-on time.
      }
      // The listed missions are skipped by id too, whatever their clock says.
      this.autoTried = skipOpenMissions(this.open$.value, this.autoTried);
      writeTried(this.autoTried);
      this.pumpAutoAccept();
    }
  }

  /** The window shows the flow briefing or the map, and no flight timer runs. */
  public autoAcceptIdle(): boolean {
    return (
      isAutoAcceptIdleRoute(this.router.url) &&
      // Lazy: FlightTimerService injects ops-control, which injects this service.
      !this.injector.get(FlightTimerService).isTimerRunning()
    );
  }

  /** The window got busy before the form opened: try `id` again later. */
  public autoAcceptDefer(id: number): void {
    if (id !== this.autoPendingId) return;
    this.autoTried.delete(id);
    writeTried(this.autoTried);
    this.autoPendingId = undefined;
  }

  /** The create for `id` ended, whatever the outcome; take the next mission. */
  public autoAcceptDone(id: number | undefined): void {
    if (id === undefined || id !== this.autoPendingId) return;
    this.autoPendingId = undefined;
    this.pumpAutoAccept();
  }

  // Refuse sends the same acknowledge as saving a flight for the mission: it
  // marks the mission read, which closes it on every screen.
  public async refuseMission(id: number): Promise<void> {
    await this.acknowledgeAlert(id, await this.settings.getSetting("registration"));
    this.open$.next(this.open$.value.filter((m) => m.id !== id));
  }

  private mdDummyMissionBody =
    "# ALARMDEPESCHE >> OEXAM \n" +
    "\n" +
    "|                            |                             |\n" +
    "|----------------------------|-----------------------------|\n" +
    "| Einsatzstichwort:          | **9 E4 TEST**               |\n" +
    "| Fallbeschreibung:          | **BEGLEITPERSON: N<br>**    |\n" +
    "| Patient Name:              | **-**                      |\n" +
    "| Sondersignal:              | **0**                      |\n" +
    "| Einsatzbeginn(Soll):       | ** **    |\n" +
    "| Einsatznummer:             | **R2022000091**            |\n" +
    "\n" +
    "## Einsatzziel\n" +
    "\n" +
    "|                    |                                                                                 |\n" +
    "|--------------------|---------------------------------------------------------------------------------|\n" +
    "| Objekt:            | **RK LV RLS STEIERMARK STRASSGANGER STRASSE 384 16.BEZ.STRASSGANG GRAZ 8054**    |\n" +
    "| Strasse / Hs.-Nr.: | **RK LV RLS STEIERMARK STRASSGANGER STRASSE 384 16.BEZ.STRASSGANG GRAZ 8054 26** |\n" +
    "| PLZ / Ort:         | **8010 Graz**                                                                   |\n" +
    "| Stadt:             | **-**                                                                           |\n" +
    "| Geopositionen:     | **geogr. Länge 15,396643 // geogr. Breite 47,037682**                             |\n" +
    "\n" +
    "\n" +
    "## Transportziel\n" +
    "\n" +
    "|                    |                                                                               |\n" +
    "|--------------------|-------------------------------------------------------------------------------|\n" +
    "| Objekt:            | **LKH VOITSBERG**                                                             |\n" +
    "| Strasse / Hs.-Nr.: | **LKH VOITSBERG 26**                                                           |\n" +
    "| PLZ / Ort:         | **8010 Graz**                                                                  |\n" +
    "| Stadt:             | **-**                                                                          |\n" +
    "| Geopositionen:     | ** **                                                                          |\n";

  private readonly DUMMY_MISSIONS = [
    {
      body: this.mdDummyMissionBody,
      destination: "11.545685582513726,47.15547890458493",
      timestamp: new Date().toISOString(),
      state: "created",
      source: "oeamtc-mailgun",
      id: 1636597240944,
    },
  ];

  // Legacy: curl https://oeamtc-mailgun-api.prod.newaydata.com/mission -X GET -H "key:…" -H "base:c8" -H "registration:oenwy"
  // mission-go (a backend with `missionApiKey`): GET /v1/mission?base=… and
  // POST /v1/mission/acknowledge, both with a Bearer key. It lists by base alone.
  public useBaseForMisssion: string = "";

  private async missionApi() {
    const info = await this.envService.getApiInfo();
    const key = info.missionApiKey;
    return {
      key,
      auth: [{ key: "Authorization", value: "Bearer " + key }],
      listUrl: info.mission + (key ? "/v1/mission" : "/mission"),
      ackUrl: info.mission + (key ? "/v1/mission/acknowledge" : "/mission/acknowledgment"),
    };
  }

  // mission-go lists by base alone, and the duty's homebase is "adhoc" for every
  // ARA duty. So there the pilot picks the base in Settings.
  private async missionBase(): Promise<string> {
    if (!(await this.missionApi()).key) return this.useBaseForMisssion;
    return (await this.settings.getSetting("base"))?.name ?? "";
  }
  // mission alerts
  public lastAlertIdDisplayed: number = -1;

  public get testMissionAvailable(): boolean {
    return ["local", "dev", "desktop-dev"].includes(this.envService.apiInfoNow?.stage);
  }

  public async triggerTestMission(): Promise<void> {
    await this.envService.getApiInfo();
    if (!this.testMissionAvailable) return;
    const mission = await this.getDummyMission();
    this.lastAlertIdDisplayed = mission.id;
    this.alert.next(mission);
  }

  public async getDummyMission(): Promise<MissionInterface> {
    // get current base
    const base = await this.settings.getSetting("base");

    let lat: number = base.center[1] + Math.floor(Math.random() * 1000) / 1000 - 0.5;
    let lon: number = base.center[0] + Math.floor(Math.random() * 1000) / 1000 - 0.5;

    this.DUMMY_MISSIONS[0].destination = lon + "," + lat;
    // A fresh id per test mission: a mission id names its flight (initFlight).
    this.DUMMY_MISSIONS[0].id = Date.now();
    this.DUMMY_MISSIONS[0].body = this.DUMMY_MISSIONS[0].body;
    return this.DUMMY_MISSIONS[0];
  }

  public async getMissionObj(): Promise<MissionInterface> {
    if (await this.inTraining()) return undefined;
    const reg = await this.settings.getSetting("registration");

    try {
      const mi = await this.getAlerts(await this.missionBase(), reg);
      return mi;
    } catch (err) {
      console.log("warn: could not fetch mission! " + err);
    }

    return undefined;
  }

  public async fetchMissionApi() {
    let registration = await this.settings.getSetting("registration");
    const base = await this.missionBase();

    this.settings.logPush("mission.service.ts: check new missions for me: " + base + " " + registration);

    let testReg = await (await this.configSer.configP).general.testDutyAircraftReg;

    // dummy mission if test reg
    if (testReg.includes(registration)) {
      let alerts = await this.getDummyMission();
      this.alert.next(alerts);
      this.lastAlertIdDisplayed = alerts.id;
    }

    if (await this.inTraining()) return;

    if ((await this.missionApi()).key) {
      // The socket pushes missions; a refresh only reconnects it.
      this.retryMs = 1000;
      await this.watchMissions(true);
      return;
    }

    try {
      const alerts = await this.getAlerts(base, registration);

      if (alerts !== undefined) {
        if (this.lastAlertIdDisplayed !== alerts.id) {
          console.log("mission.service.ts: got a mission, sending event: " + JSON.stringify(alerts));
          this.alert.next(alerts);
          this.lastAlertIdDisplayed = alerts.id;
        } else {
          console.log("mission.service.ts: had that mission already: " + alerts.id);
          this.settings.logPush("mission.service.ts: had that mission already: " + alerts.id);
        }
      } else {
        this.settings.logPush("mission.service.ts: no missions in the last 30 min. ");
      }
    } catch (err) {
      this.settings.logPush("could not fetch mission. " + err);
    }
  }

  public async fetchLastMissionAgain() {
    this.lastAlertIdDisplayed = -1;
    await this.fetchMissionApi();
  }

  public async acknowledgeAlert(alertId: number, registration: string): Promise<any> {
    const api = await this.missionApi();
    if (api.key) {
      const res = await this.com.post(api.auth, [], "", api.ackUrl, { id: String(alertId), registration });
      if (!res?.success) throw new Error("mission acknowledge refused");
      return {};
    }
    return new Promise((resolve, reject) => {
      const query = [
        {
          key: "id",
          value: alertId.toString(),
        },
        {
          key: "registration",
          value: registration,
        },
      ];
      this.com
        .get(query, query, "", api.ackUrl)
        .then((res) => {
          if (res.success) {
            resolve({});
          } else {
            reject();
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public castMission2rescuetrack(mission: any): any {
    const d = new Date(mission.timestamp);
    const point = mission.destination.split(",").map((c) => {
      return parseFloat(c);
    });

    return {
      alertId: mission.id,
      alertState: "Transmitted",
      alertTimestamp: d.getTime(),
      connectorObjectId: -1,
      mission: {
        incidentId: -1,
        missionId: -1,
        destination: {
          objectId: null,
          object: null,
          place: null,
          subPlace: null,
          street: null,
          houseNumber: null,
          zipCode: null,
          coordinateSource: null,
        },
        site: {
          objectId: null,
          object: null,
          place: "No Rescuetrack mission",
          subPlace: null,
          street: null,
          houseNumber: null,
          zipCode: null,
          coordinateSource: null,
        },
        patient: {
          name: null,
          surname: null,
          place: null,
          subPlace: null,
          street: null,
          houseNumber: null,
          insuranceNumber: null,
          insuranceIK: null,
          sex: null,
          birthday: null,
          PZC: null,
        },
        general: {
          signal: null,
          keyword: [],
          eventKeyword: null,
          keywordCode: [],
          diagnosis: null,
          note: null,
          remark: null,
          protocol: null,
          RMZ: null,
          abrCrew: [],
          delayMoveOut: null,
          delayApproach: null,
        },
        coordinates: point,
      },
    };
  }

  public getAlerts(base: string, registration: string): Promise<MissionInterface> {
    return new Promise((resolve, reject) => {
      this.requestAlerts(base, registration)
        .then((alerts) => {
          resolve(alerts[alerts.length - 1]);
        })
        .catch((err) => {
          console.error("err #aafed");
          reject(err);
        });
    });
  }

  /** The latest mission of a base for a crew display: acknowledged missions of the
   *  last 12 h count too (mission-go `includeRead`), because the aircraft
   *  acknowledges the mission the crew is flying within seconds. */
  public async getCrewMission(base?: string): Promise<MissionInterface | undefined> {
    const missions = await this.requestAlerts(base || (await this.missionBase()), "", true);
    return missions[missions.length - 1];
  }

  private async requestAlerts(base: string, registration: string, includeRead = false): Promise<MissionInterface[]> {
    const api = await this.missionApi();
    if (api.key) {
      // The registration hides the missions this aircraft acknowledged; mission-go
      // v0.6.x returns them for 30 minutes otherwise, and the app alerts them again.
      const query = [{ key: "base", value: base }];
      if (registration) query.push({ key: "registration", value: registration });
      if (includeRead) query.push({ key: "includeRead", value: "true" });
      const res = await this.com.get(api.auth, query, "", api.listUrl);
      if (!res) throw new Error("mission list failed");
      // mission-go sends its uint32 id as a string; the app keys missions by number.
      // Oldest first, as the legacy API answers: getAlerts takes the last one.
      return ((res.missions ?? []) as MissionInterface[])
        .map((m) => ({ ...m, id: Number(m.id) }))
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    }
    const query = [
      {
        key: "base",
        value: base,
      },
      {
        key: "registration",
        value: registration,
      },
    ];

    const res = await this.com.get([], query, "", api.listUrl);
    if (res.success) {
      return res.missions;
    } else {
      return Promise.reject();
    }
  }
}

export interface MissionInterface {
  body: string;
  destination: string; // "15.615,48.4125",
  timestamp: string; // "2021-11-11T02:20:43.000Z",
  state: string; // "created",
  source: string; // "oeamtc-mailgun",
  id: number; // 1636597240944
}

// sessionStorage is shared by the same-origin iframes of one tab, but only the
// auto-accept window reads or writes this key. The flag itself stays in
// window.name, which is per iframe.
function readTried(): number[] {
  try {
    const ids = JSON.parse(sessionStorage.getItem(AUTO_ACCEPT_TRIED_KEY) ?? "[]");
    return Array.isArray(ids) ? ids.filter((id) => typeof id === "number") : [];
  } catch {
    return [];
  }
}

function readSince(): number {
  try {
    return Number(sessionStorage.getItem(AUTO_ACCEPT_SINCE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeTried(ids: Set<number>): void {
  try {
    sessionStorage.setItem(AUTO_ACCEPT_TRIED_KEY, JSON.stringify([...ids]));
  } catch {
    // ponytail: without storage a reload can create a mission's flight again.
  }
}
